import {
  useI18n
} from "@/composables/useI18n";
import type { ImportResult } from "@/scripts/Common/types";
import {ref} from "vue";
import type {Ref} from "vue";
const useSnackbar = (snackbarRef: Ref) => {
  const {
    t,
  } = useI18n()

  const snackbarStatus = ref('');
  const snackbarText = ref<string[]>([]);

  type OperationActionMap = (operationType: string) => string;

  interface SnackbarOptions {
    statusTextSuccessI18nKey?: string;
    statusTextFailedItemsI18nKey?: string;
  }

  const showSnackbar = (
    {
      status = undefined,
      statusText = undefined
    }: {
      status?: string,
      statusText?: string[]
    }
  ) => {
    snackbarStatus.value = status ? status : 'success';
    snackbarText.value = statusText ? statusText : [t('ErrorItemDialog.success')];

    (snackbarRef.value as any).show();
  };

  /**
   * Shows a snackbar with the response of an operation. Only works with a OperationResultDto<T> of the backend.
   * @param response - response object. Must have the following properties: successfulItems, failedItems, operationType
   * @param callback - function that maps operationType of the response to a string that describes the action
   */
  const showSnackbarResponse = (response: Record<any, any>, callback: OperationActionMap) => {

    const { successfulItems, failedItems, operationType } = response;

    const action = callback(operationType);

    const statusText = [];
    let status = 'success';

    if (successfulItems.length > 0) {
      const count = successfulItems.length;
      statusText.push(t('ErrorItemDialog.successful_items', count, { action: action }) as string);
    }

    if (failedItems.length > 0) {
      status = 'orange darken-1';
      const count = failedItems.length;
      statusText.push(t('ErrorItemDialog.failed_items', count, { action: action }) as string);
    }

    showSnackbar({status, statusText})
  }

  const showSnackbarForImportResult = (
    result: ImportResult,
    options: SnackbarOptions = {}
  ) => {
    const {
      statusTextSuccessI18nKey = 'successful_items',
      statusTextFailedItemsI18nKey = 'failed_items',
    } = options;

    const statusText: string[] = [];
    const status = result.failedImports > 0 ? 'orange darken-1' : 'success';

    if (result.successfulImports > 0) {
      statusText.push(t(statusTextSuccessI18nKey, result.successfulImports) as string);
    }

    if (result.failedImports > 0) {
      statusText.push(t(statusTextFailedItemsI18nKey, result.failedImports) as string);
    }

    showSnackbar({status, statusText})
  }

  return {
    snackbarStatus,
    snackbarText,
    showSnackbarResponse,
    showSnackbarForImportResult,
    showSnackbar,
  }

}


export {
  useSnackbar
}
